import React, { useState } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ChevronLeft,
  Calendar as CalendarIcon,
  MapPin,
  Star,
  Minus,
  Plus,
  CheckCircle2,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { motion } from "framer-motion";

/**
 * @param {{
 *   hotel: {
 *     image: string;
 *     name: string;
 *     area: string;
 *     rating: number;
 *     price_per_night: number;
 *     description: string;
 *     amenities: string[];
 *     room_type: string;
 *   };
 *   initialCheckin?: Date | null;
 *   initialCheckout?: Date | null;
 *   initialGuests?: number;
 *   onBack: () => void;
 *   onProceedToCheckout: (checkin: Date, checkout: Date, guests: number, nights: number, totalAmount: number) => void;
 * }} props
 */
export default function AccommodationDetail({
  hotel,
  initialCheckin,
  initialCheckout,
  initialGuests,
  onBack,
  onProceedToCheckout,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const PopoverContentComp = /** @type {React.ComponentType<any>} */ (PopoverContent);

  const [checkin, setCheckin] = useState(initialCheckin || null);
  const [checkout, setCheckout] = useState(initialCheckout || null);
  const [guests, setGuests] = useState(initialGuests || 2);

  const nights = checkin && checkout ? Math.max(1, differenceInDays(checkout, checkin)) : 0;
  const totalAmount = nights * hotel.price_per_night;

  const canProceed = checkin && checkout && nights > 0;

  const handleProceed = () => {
    if (canProceed && checkin && checkout) {
      onProceedToCheckout(checkin, checkout, guests, nights, totalAmount);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={onBack}
            className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Search
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative h-80 rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src={hotel.image}
                alt={hotel.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h1 className="text-3xl font-bold mb-2">{hotel.name}</h1>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm">{hotel.area}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-[#D4AF37] fill-current" />
                    <span className="text-sm font-semibold">{hotel.rating}</span>
                  </div>
                  <span className="text-sm text-white/70">{hotel.room_type}</span>
                </div>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h2 className="text-xl font-bold mb-3 text-[#0B2421]">About this property</h2>
                <p className="leading-relaxed text-gray-600 whitespace-pre-line">{hotel.description}</p>
              </Card>
            </motion.div>

            {/* Amenities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h2 className="text-xl font-bold mb-4 text-[#0B2421]">Amenities</h2>
                <div className="grid grid-cols-2 gap-3">
                  {hotel.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <span className="text-gray-600 text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Right Column - Booking Widget */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="sticky top-24"
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-md">
                <div className="mb-6">
                  <div className="text-sm mb-1 text-gray-500">Price per night</div>
                  <div className="text-4xl font-bold text-[#D4AF37]">
                    SGD {hotel.price_per_night}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Check-in */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">Check-in</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-start gap-2 h-12 rounded-lg border border-gray-200 bg-white px-4 text-[#0B2421] hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 text-gray-500" />
                          <span className={checkin ? "text-gray-700" : "text-gray-400"}>
                            {checkin ? format(checkin, "dd MMM yyyy") : "Select check-in"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContentComp className="w-auto p-0" align="start">
                        <Calendar
                          className=""
                          classNames={{}}
                          mode="single"
                          selected={checkin}
                          onSelect={setCheckin}
                          disabled={/** @param {Date} d */ (d) => d < new Date()}
                        />
                      </PopoverContentComp>
                    </Popover>
                  </div>

                  {/* Check-out */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">Check-out</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-start gap-2 h-12 rounded-lg border border-gray-200 bg-white px-4 text-[#0B2421] hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 text-gray-500" />
                          <span className={checkout ? "text-gray-700" : "text-gray-400"}>
                            {checkout ? format(checkout, "dd MMM yyyy") : "Select check-out"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContentComp className="w-auto p-0" align="start">
                        <Calendar
                          className=""
                          classNames={{}}
                          mode="single"
                          selected={checkout}
                          onSelect={setCheckout}
                          disabled={/** @param {Date} d */ (d) => !checkin || d <= checkin || d < new Date()}
                        />
                      </PopoverContentComp>
                    </Popover>
                  </div>

                  {/* Guests */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">Guests</label>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setGuests(Math.max(1, guests - 1))}
                        disabled={guests <= 1}
                        className="h-12 w-12 bg-[#0B2421] hover:bg-[#0B2421]/85 border-none text-white"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="flex-1 text-center">
                        <div className="text-2xl font-bold text-[#0B2421]">{guests}</div>
                        <div className="text-xs text-gray-500">Guests</div>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setGuests(Math.min(8, guests + 1))}
                        disabled={guests >= 8}
                        className="h-12 w-12 bg-[#0B2421] hover:bg-[#0B2421]/85 border-none text-white"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Price Calc */}
                  {nights > 0 && (
                    <div className="pt-4 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          SGD {hotel.price_per_night} × {nights} night{nights > 1 ? "s" : ""}
                        </span>
                        <span className="font-medium text-[#0B2421]">SGD {totalAmount}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-100">
                        <span className="text-[#0B2421]">Total</span>
                        <span className="text-[#D4AF37]">SGD {totalAmount}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleProceed}
                    disabled={!canProceed}
                    className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg rounded-xl disabled:opacity-50"
                  >
                    Proceed to Secure Checkout
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
